import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'seamless-immutable';
import moment from 'moment';
import FileSaver from 'file-saver';

import DataTransformers from '../data-transformers/data-transformers';
import { LoadingIndicator } from '../../../components';
import WidgetWrapper from '../widget-wrapper';
import prepareVisualizerOptions from '../../../util/prepare-visualizer-options';
import formatNumber from '../../../util/format-number';
import forceFloat from '../../../util/force-float';

import sortData from '../../../util/sort-data';
import VisualizerSelector from '../data-visualizers';

const styles = {
  LoadingIndicatorWrapper: {
    minHeight: '100px',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

class Widget extends Component {
  constructor(props) {
    super(props);

    const {
      clientDataTransformer, clientDataTransformerOptions, clientDataVisualizer, clientDataVisualizerOptions, currencySymbol,
    } = props;

    const dataTransformer = DataTransformers.get(clientDataTransformer);
    const transformerOptions = JSON.parse(clientDataTransformerOptions);

    const visualizerOptions = prepareVisualizerOptions(clientDataVisualizerOptions, clientDataVisualizer, currencySymbol);
    const { alternatives } = visualizerOptions;

    this.state = {
      dataTransformer: dataTransformer ? dataTransformer() : null,
      transformerOptions,
      visualizerOptions,
      alternatives: alternatives || [],
    };
  }

  getSelectedAlternativeData(reportData, selectedAlternative) {
    if (!selectedAlternative || !selectedAlternative.key) {
      return reportData;
    }

    return reportData.map((r) => {
      const { alternativeData } = r;
      const data = alternativeData ? alternativeData[selectedAlternative.key] : [];

      return {
        ...r,
        data: data || [],
      };
    });
  }

  setNextAlternative = () => {
    const { alternatives } = this.state;

    if (alternatives.length < 2) {
      return;
    }

    this.setState({
      alternatives: alternatives.concat(alternatives.splice(0, 1)),
    });
  }

  dataIsEmpty(dataArray) {
    return !dataArray || dataArray.every(data => data.length < 1 || data.data?.length < 1);
  }

  fetchReportData(reportDataId, dateRange, locationId) {
    const {
      loadReportData, reportWidgetID,
    } = this.props;

    const nextDay = moment().add(1, 'days').set('hours', 5).set('minutes', 0)
      .startOf('minutes')
      .unix();

    const isFuture = dateRange.startDate >= nextDay;

    const payload = {
      reportDataId,
      startDate: isFuture
        ? moment.unix(dateRange.startDate).subtract(1, 'year').valueOf()
        : dateRange.startDate * 1000, // times thousand to get from seconds to milliseconds
      endDate: isFuture
        ? moment.unix(dateRange.endDate).subtract(1, 'year').valueOf()
        : dateRange.endDate * 1000,
      widgetId: reportWidgetID,
      options: {
        locationId,
      },
    };

    loadReportData(payload);
  }

  exportCsv(data, transformedData) {
    const { title, clientDataTransformer, clientDataVisualizerOptions } = this.props;
  
    const csvLine = columns => `"${columns.join('";"')}"\r\n`;
  
    const visualizerOptions = JSON.parse(clientDataVisualizerOptions);

    const useTransformedData = (clientDataTransformer === 'InvoiceServiceDataTransformer' || visualizerOptions.exportTransformedData);
    const d = useTransformedData ? transformedData[0] : data[0];
    debugger

    let body = '';

    const hasName = !useTransformedData && !!d.data[0].name;
    const additionalData = useTransformedData ? transformedData.slice(1) : data.slice(1);

    const { exportPattern } = visualizerOptions;

    if (exportPattern) {
      const compareColumns = exportPattern.filter(x => x.hasComparision);

      let headers =  exportPattern.map(x => x.value);
      // Generate headers only if additionalData is present
      let additionalHeaders = [];
      if (additionalData.length > 0) {
        for (let i = 0; i < additionalData.length; i++) {

          compareColumns.forEach(element => {
            additionalHeaders.push(`${element.value} Vgl. Zeitraum ${i + 1}`);  
          });


          compareColumns.forEach(element => {
            additionalHeaders.push(`% Abweichung ${element.value} Vgl. Zeitraum ${i + 1}`);  
          });           
        }
      }
  
      body += csvLine([...headers, ...additionalHeaders]);
  

      d.forEach(line => {
        let lineItems = [];
        exportPattern.forEach(x => {
          lineItems.push(formatNumber(line[x.key]));          
        });


        if (line.comparisons) {
          line.comparisons.forEach(comparision => {
            compareColumns.forEach(element => {
              const compareValue = comparision[element.key];
      
              lineItems.push(formatNumber(compareValue || 0));
            });
  
            compareColumns.forEach(element => {
              const compareValue = comparision[element.key] || 0;
              const currentValue = line[element.key];
      
              if (compareValue == 0) {
                lineItems.push('-');  
              }
              lineItems.push(formatNumber(((currentValue - compareValue) / compareValue) * 100));
            });          
          })
        }

        body += csvLine(lineItems);
      });

      const BOM = '\uFEFF';
      const csvData = (BOM + body).replace(/(<|&lt;)br\s*\/*(>|&gt;)/g, '\n');
  
      const blob = new Blob([csvData], {
        type: 'text/csv;charset=utf-8',
      });
      FileSaver.saveAs(blob, `${title}.csv`);

      return;
    }


    const priceColumnIndex = d.columns.findIndex(x => x === 'Preis');
    const amountColumnIndex = d.columns.findIndex(x => x === 'Stück');
    const articleNumberColumnIndex = d.columns.findIndex(x => x === 'Artikelnummer' || x === 'ArtikelNr' || x === 'ArtikelNummer');
  
    // Generate headers only if additionalData is present
    let additionalHeaders = [];
    if (additionalData.length > 0) {
      for (let i = 0; i < additionalData.length; i++) {
        additionalHeaders.push(`Umsatz Vgl. Zeitraum ${i + 1}`);
        additionalHeaders.push(`Stück Vgl. Zeitraum ${i + 1}`);        
        additionalHeaders.push(`% Abweichung Umsatz Vgl. Zeitraum ${i + 1}`);
        additionalHeaders.push(`% Abweichung Stück Vgl. Zeitraum ${i + 1}`);        
      }
    }
  
    body += csvLine((hasName ? ['Name'] : []).concat(d.columns, additionalHeaders));
  
    // Map additionalData only if it is present
    const additionalDataMap = new Map();
    if (additionalData.length > 0) {
      additionalData.forEach((element, index) => {
        element.data.forEach(line => {
          additionalDataMap.set(line.values[articleNumberColumnIndex], line);
        });
      });
    }
  
    const numberBlacklist = ['ID', 'Monat', 'FirmenNr', 'MitarbeiterId', 'Lohnart', 'Personalnummer', 'Verrechnungsnummer', 'KartenId'];
  
    d.data.forEach(line => {
      let lineValues = line.values;
      const artikelnummer = line.values[articleNumberColumnIndex];
  
      if (additionalDataMap.has(artikelnummer)) {
        const additionalLine = additionalDataMap.get(artikelnummer);

        const newPrice = forceFloat(lineValues[priceColumnIndex]);
        const newAmount = forceFloat(lineValues[amountColumnIndex]);

        const price = forceFloat(additionalLine.values[priceColumnIndex]);
        const amount = forceFloat(additionalLine.values[amountColumnIndex]);

        lineValues = lineValues.concat([formatNumber(newPrice)]);
        lineValues = lineValues.concat([formatNumber(newAmount)]);

        lineValues = lineValues.concat([formatNumber(((newPrice - price) / price) * 100)]);
        lineValues = lineValues.concat([formatNumber(((newAmount - amount) / amount) * 100)]);

      } else if (additionalData.length > 0) {
        lineValues = lineValues.concat(0);
        lineValues = lineValues.concat(0);
      }
  
      body += csvLine((hasName ? [line.name] : []).concat(lineValues.map((x, i) => {
        const colName = line.name || d.columns ? d.columns[i] : '';
        if (numberBlacklist.includes(colName)) {
          return x;
        }
        return formatNumber(x);
      })));
    });
  
    // Process additionalData if present
    if (additionalData.length > 0) {
      additionalData.forEach(element => {
        element.data.forEach(line => {
          if (!d.data.some(dLine => dLine.values[0] === line.values[0])) {
            let lineValues = new Array(d.columns.length).fill(0); // Filling with zeroes based on d.columns length
            lineValues = lineValues.concat([line.values[priceColumnIndex]]);
            lineValues = lineValues.concat([line.values[amountColumnIndex]]);
  
            body += csvLine((hasName ? [line.name] : []).concat(lineValues.map((x, i) => {
              const colName = line.name || d.columns ? d.columns[i] : '';
              if (numberBlacklist.includes(colName)) {
                return x;
              }
              return formatNumber(x);
            })));
          }
        });
      });
    }
  
    const BOM = '\uFEFF';
    const csvData = (BOM + body).replace(/(<|&lt;)br\s*\/*(>|&gt;)/g, '\n');

    const blob = new Blob([csvData], {
      type: 'text/csv;charset=utf-8',
    });
    FileSaver.saveAs(blob, `${title}.csv`);
  }

  render() {
    const {
      title, width, container, reportData, isLoading, inApp, clientDataVisualizer, reportDataId, gridColumn, gridRow,
      useDesktopLayout, locationId, isMultiLocationReport, locationIds, reportWidgetID,
    } = this.props;

    const {
      dataTransformer, transformerOptions, visualizerOptions, alternatives,
    } = this.state;

    const selectedAlternative = alternatives[0];
    const mappedReportData = this.getSelectedAlternativeData(reportData, selectedAlternative);

    let content = null;
    let transformedData = null;

    if (isLoading) {
      content = (
        <div style={styles.LoadingIndicatorWrapper}>
          <LoadingIndicator />
        </div>
      );
    } else if (mappedReportData.length < 1) {
      content = <span>Leider sind keine Daten verfügbar</span>;
    } else if (dataTransformer) {
      const mutableMappedReportData = Immutable.asMutable(mappedReportData, { deep: true })
        .sort((a, b) => a.reportDataId - b.reportDataId);

      transformedData = dataTransformer.transformMultiple(
        mutableMappedReportData,
        transformerOptions,
        isMultiLocationReport,
        locationIds,
      );
      if (this.dataIsEmpty(transformedData)) {
        content = <span>Für den gewählten Zeitraum sind keine Daten verfügbar</span>;
      } else {
        sortData(transformedData[0], visualizerOptions);

        content = (
          <VisualizerSelector
            visualizerName={clientDataVisualizer}
            data={transformedData}
            options={visualizerOptions}
            reportDataId={reportDataId}
            title={title}
            locationId={locationId}
            useDesktopLayout={useDesktopLayout}
            inApp={inApp}
            reportWidgetID={reportWidgetID}
          />
        );
      }
    } else {
      content = <span>DataTransformer konnte nicht gefunden werden</span>;
    }

    return (
      <WidgetWrapper
        title={title}
        container={container}
        width={width}
        inApp={inApp}
        visualizerOptions={visualizerOptions}
        setVisualizerOptions={newOptions => this.setState({ visualizerOptions: newOptions })}
        alternative={selectedAlternative ? selectedAlternative.name : null}
        onNextAlternative={this.setNextAlternative}
        exportCsv={() => this.exportCsv(mappedReportData, transformedData)}
        dataIsEmpty={isLoading || this.dataIsEmpty(transformedData)}
        gridColumn={gridColumn}
        gridRow={gridRow}
      >
        {content}
      </WidgetWrapper>
    );
  }
}

Widget.propTypes = {
  title: PropTypes.string,
  reportWidgetID: PropTypes.number.isRequired,
  width: PropTypes.number,
  container: PropTypes.number,
  clientDataTransformer: PropTypes.string,
  clientDataTransformerOptions: PropTypes.string,
  clientDataVisualizer: PropTypes.string,
  clientDataVisualizerOptions: PropTypes.string,
  isLoading: PropTypes.bool,
  inApp: PropTypes.bool,
  reportData: PropTypes.array,
  currencySymbol: PropTypes.string.isRequired,
  reportDataId: PropTypes.number,
  loadReportData: PropTypes.func.isRequired,
  gridColumn: PropTypes.number,
  gridRow: PropTypes.number,
  locationId: PropTypes.number,
  useDesktopLayout: PropTypes.bool.isRequired,
  isMultiLocationReport: PropTypes.bool.isRequired,
};

Widget.defaultProps = {
  title: 'Widget',
  width: 12,
  container: 1,
  clientDataTransformer: '',
  clientDataTransformerOptions: '',
  clientDataVisualizer: '',
  clientDataVisualizerOptions: '',
  isLoading: false,
  reportData: [],
  inApp: false,
  reportDataId: null,
  gridColumn: null,
  gridRow: null,
  locationId: null,
};

export default Widget;
